import type { ReadonlyVec3, vec2 } from "gl-matrix";
import { vec3 } from "gl-matrix";

import type { AnchoredSvgData } from "features/engine2D/svgRenderer/types";

import type { MarkerIcon, MarkerMarkupBadge } from ".";
import type { MarkerBounds, MarkerCamera, MarkerScreenPosition } from "./types";

/**
 * The visible content width of the pin shape in viewBox coordinates.
 * Derived from the mask region in createPinShapedMarker (`x="0" width="36"`).
 * Used as the scaling reference so that `size` controls the rendered visual width of the pin.
 */
const PIN_CONTENT_WIDTH = 36;

/**
 * The anchor offset for the default pin SVG, expressed in the viewBox coordinate space
 * (`viewBox="0 0 57 56"`).
 *
 * The pin's pointy tip sits at approximately (18, 54) in viewBox coordinates — read directly
 * from the converging chevron path points in createPinShapedMarker. This offset is the
 * negative of that tip position: vec2(-18, -54) shifts the SVG left by 18 units and up by
 * 54 units so the tip lands exactly on the 3D anchor point when using
 * `anchorOffsetCoordinateType: "viewBox"`.
 *
 * If the pin SVG shape or its viewBox ever changes, update this to match the new tip
 * coordinates (negated). An incorrect value causes the "drag shift" bug where a dropped
 * pin jumps away from the cursor's release position.
 */
export const DEFAULT_PIN_ANCHOR_OFFSET: vec2 = [-18, -54];

/**
 * Default pin size in pixels when rendered (width dimension).
 */
export const DEFAULT_PIN_SIZE = 34;

/**
 * Lightens a hex color by a given amount
 * @param hexColor Hex color string (e.g., "#2196F3")
 * @param amount Amount to lighten (0-1)
 */
export function lightenColor(hexColor: string, amount: number): string {
    // Remove # if present
    const hex = hexColor.replace("#", "");

    // Parse RGB
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    // Lighten
    const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    const newB = Math.min(255, Math.floor(b + (255 - b) * amount));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

/**
 * Darkens a hex color by a given amount
 * @param hexColor Hex color string (e.g., "#2196F3")
 * @param amount Amount to darken (0-1, where 0 = no change, 1 = black)
 * @returns Darkened hex color string
 */
export function darkenColor(hexColor: string, amount: number): string {
    // Remove # if present
    const hex = hexColor.replace("#", "");

    // Parse RGB
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    // Darken by moving towards black (0)
    const newR = Math.max(0, Math.round(r * (1 - amount)));
    const newG = Math.max(0, Math.round(g * (1 - amount)));
    const newB = Math.max(0, Math.round(b * (1 - amount)));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
}

const PREFIX = "marker";
const SEPARATOR = ":";

/**
 * Creates a unique composite string ID from collection and marker IDs.
 * Encodes components to safely handle special characters (like colons).
 */
export function createCompositeId(collectionId: string, markerId: string): string {
    return `${PREFIX}${SEPARATOR}${encodeURIComponent(collectionId)}${SEPARATOR}${encodeURIComponent(markerId)}`;
}

/**
 * Parses a composite string ID back into collection and marker IDs.
 * Returns null if the format is invalid.
 */
export function parseCompositeId(compositeId: string): { collectionId: string; markerId: string } | null {
    const parts = compositeId.split(SEPARATOR);
    if (parts.length !== 3 || parts[0] !== PREFIX) {
        return null;
    }
    try {
        return {
            collectionId: decodeURIComponent(parts[1]),
            markerId: decodeURIComponent(parts[2]),
        };
    } catch {
        // Malformed percent-encoding; treat as invalid composite ID
        return null;
    }
}

function createScreenPositionDataforSvg(
    svgElement: Element,
    canvas: HTMLCanvasElement,
    markerPosition: ReadonlyVec3,
    camera: MarkerCamera,
): MarkerScreenPosition | undefined {
    const elementRect = svgElement.getBoundingClientRect();
    if (elementRect.width === 0 || elementRect.height === 0) {
        return undefined;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const bounds: MarkerBounds = {
        top: elementRect.top - canvasRect.top,
        bottom: elementRect.bottom - canvasRect.top,
        left: elementRect.left - canvasRect.left,
        right: elementRect.right - canvasRect.left,
        width: elementRect.width,
        height: elementRect.height,
    };

    const x = bounds.left + bounds.width / 2;
    const y = bounds.top + bounds.height / 2;

    const distance = vec3.distance(camera.position as vec3, markerPosition as vec3);
    const range = camera.far - camera.near;
    const depth = range > 0 ? Math.max(0, Math.min(1, (distance - camera.near) / range)) : 0;

    return { x, y, depth, bounds };
}

export function resolveMarkerScreenPosition(
    markerElement: Element | null | undefined,
    compositeId: string,
    canvas: HTMLCanvasElement,
    markerPosition: ReadonlyVec3,
    camera: MarkerCamera,
): MarkerScreenPosition | undefined {
    const fromExplicit = markerElement
        ? createScreenPositionDataforSvg(markerElement, canvas, markerPosition, camera)
        : undefined;
    if (fromExplicit) {
        return fromExplicit;
    }

    const escapedId = compositeId.replace(/"/g, '\\"');
    const fresh = document.querySelector(`[data-id="${escapedId}"]`);
    if (!fresh) {
        return undefined;
    }
    return createScreenPositionDataforSvg(fresh, canvas, markerPosition, camera);
}

export function scheduleEnlargedMeasurement({
    isStillTarget,
    measure,
    onDelivered,
    deliver,
}: {
    isStillTarget?: () => boolean;
    measure: () => MarkerScreenPosition | undefined;
    onDelivered?: () => void;
    deliver: (enlargedBounds: MarkerBounds | undefined) => void;
}): { cancel: () => void } {
    let raf2: number | null = null;
    const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
            if (isStillTarget && !isStillTarget()) {
                return;
            }
            const measured = measure();
            onDelivered?.();
            deliver(measured?.bounds);
        });
    });

    return {
        cancel: () => {
            cancelAnimationFrame(raf1);
            if (raf2 !== null) {
                cancelAnimationFrame(raf2);
            }
        },
    };
}

// Options type for marker icons where event handlers (onClick/onPointerEnter/onPointerOut/onPointerDown) require an id
type IconOptions =
    | {
          color: string;
          size: number;
          opacity: number;
          useMarkupBadge: MarkerMarkupBadge;
          stroke: string;
          strokeWidth?: number;
          cursor?: string;
          alignToPinHead?: boolean;
          id?: string | { collectionId: string; markerId: string };
          onClick?: never;
          onPointerEnter?: never;
          onPointerOut?: never;
          onPointerDown?: never;
      }
    | ({
          color: string;
          size: number;
          opacity: number;
          useMarkupBadge: MarkerMarkupBadge;
          stroke: string;
          strokeWidth?: number;
          cursor?: string;
          alignToPinHead?: boolean;
          id: string | { collectionId: string; markerId: string }; // Required for interactive markers
      } & (
          | {
                onClick: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut: (id: string, event: PointerEvent) => void;
                onPointerDown?: (id: string, event: PointerEvent) => void;
            }
          | {
                onClick?: (id: string, event: PointerEvent) => void;
                onPointerEnter?: (id: string, event: PointerEvent) => void;
                onPointerOut?: (id: string, event: PointerEvent) => void;
                onPointerDown: (id: string, event: PointerEvent) => void;
            }
      ));

export function addIconToSvgData(
    data: AnchoredSvgData,
    icon: MarkerIcon | null,
    position: vec3,
    options: IconOptions,
): void {
    const {
        color,
        size,
        opacity,
        stroke,
        strokeWidth,
        cursor,
        alignToPinHead,
        id,
        onClick,
        onPointerEnter,
        onPointerOut,
        onPointerDown,
    } = options;

    // Convert structured ID to composite string for SVG renderer
    let compositeId: string | undefined;
    if (typeof id === "string") {
        compositeId = id;
    } else if (id) {
        compositeId = createCompositeId(id.collectionId, id.markerId);
    }

    if (!icon) {
        data.svgContents!.push({
            anchor: position,
            content: createPinShapedMarker(null, { useMarkupBadge: options.useMarkupBadge }),
            opacity: opacity,
            scale: size / PIN_CONTENT_WIDTH,
            useInstancing: true,
            cssVars: {
                "--pin-fill": color,
                "--pin-stroke": stroke,
            },
            // Shifts the SVG so its tip (at x=18, y=54 in the 63×72 pin coordinate space)
            // aligns with the 3D anchor point. Prevents visual jump on drag-drop.
            anchorOffset: DEFAULT_PIN_ANCHOR_OFFSET,
            anchorOffsetCoordinateType: "viewBox",
            cursor: cursor,
            ...(compositeId !== undefined && {
                id: compositeId,
                onClick,
                onPointerEnter,
                onPointerOut,
                onPointerDown,
            }),
        });

        return;
    }

    if ("svgContent" in icon) {
        data.svgContents!.push({
            anchor: position,
            content: icon.svgContent,
            opacity: opacity,
            scale: 1,
            anchorOffset: icon.anchorOffset,
            // Preserve the caller-supplied coordinate type when present.
            // When absent, infer the default from the shape of svgContent:
            //   - Full <svg> element  → "viewBox" (matches the pre-existing implicit behaviour and
            //     is correct because the renderer can parse viewBox+dimensions for scaling).
            //   - Bare fragment (<g>, <path>, etc.) → "screen" (no <svg> root to parse, so the
            //     renderer's fallback 1:1 mapping is the only valid interpretation).
            anchorOffsetCoordinateType:
                icon.anchorOffsetCoordinateType ??
                (icon.svgContent.trimStart().startsWith("<svg") ? "viewBox" : "screen"),
            cursor: cursor,
            ...(compositeId !== undefined && {
                id: compositeId,
                onClick,
                onPointerEnter,
                onPointerOut,
                onPointerDown,
            }),
        });
    } else if ("shape" in icon) {
        switch (icon.shape) {
            case "pin":
                data.svgContents!.push({
                    anchor: position,
                    content: createPinShapedMarker(icon, { useMarkupBadge: options.useMarkupBadge }),
                    opacity: opacity,
                    scale: size / PIN_CONTENT_WIDTH,
                    useInstancing: true,
                    cssVars: {
                        "--pin-fill": color,
                        "--pin-stroke": stroke,
                    },
                    // Shifts the SVG so its tip (at x=18, y=54 in the pin's SVG viewBox coordinate
                    // space, `viewBox="0 0 57 56"`) aligns with the 3D anchor point. Prevents visual
                    // jump on drag-drop.
                    anchorOffset: DEFAULT_PIN_ANCHOR_OFFSET,
                    anchorOffsetCoordinateType: "viewBox",
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
            case "circle": {
                // Align motion circle to the pin head when requested.
                // In the pin viewBox (`0 0 57 56`), tip Y is 54 and head center Y is 31 => 23 units up.
                // Head radius is 16 units in viewBox space. Convert both using the current pin scale.
                const pinScale = size / PIN_CONTENT_WIDTH;
                const alignedCircleRadius = 16 * pinScale;
                const alignedCircleOffsetY = -23 * pinScale;
                const effectiveStrokeWidth = alignToPinHead ? 1.75 : (strokeWidth ?? 1);
                data.ellipses!.push({
                    anchor: position,
                    ...(alignToPinHead ? { anchorOffset: [0, alignedCircleOffsetY] as vec2 } : {}),
                    radius: alignToPinHead ? [alignedCircleRadius, alignedCircleRadius] : [size / 2, size / 2],
                    fill: color,
                    opacity: opacity,
                    stroke: stroke ?? "#000000",
                    strokeWidth: effectiveStrokeWidth,
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
            }
            case "square":
                data.rects!.push({
                    anchor: position,
                    width: size,
                    height: size,
                    fill: color,
                    opacity: opacity,
                    stroke: stroke,
                    strokeWidth: strokeWidth,
                    cursor: cursor,
                    ...(compositeId !== undefined && {
                        id: compositeId,
                        onClick,
                        onPointerEnter,
                        onPointerOut,
                        onPointerDown,
                    }),
                });
                break;
        }
    }
}

function createPinShapedMarker(icon: MarkerIcon | null, options: { useMarkupBadge: MarkerMarkupBadge }): string {
    const iconSvgString = icon && "icon" in icon ? icon.icon : "";
    const markupBadgeSvgString =
        options.useMarkupBadge === true
            ? `
            <circle r="10" cx="35" cy="18" fill="#d2e2ff"/>
            <path fill="#0a1e40" d="m32.08 23.35-1.14-1.23V15h2.28v7.12zM32.73 12.5c.27 0 .5.24.5.53v1.13h-2.29v-1.13c0-.29.22-.53.5-.53zM38.83 21.06c0 .64-.52 1.16-1.16 1.16h-3.14l-1.1 1.1h4.24a2.27 2.27 0 1 0 0-4.53h-1.15a.6.6 0 1 1 0-1.18h.86c1.1 0 1.99-.88 1.99-1.98v-1.4h-1.1v1.4c0 .49-.4.88-.89.88h-.86a1.7 1.7 0 1 0 0 3.39h1.15c.64 0 1.16.52 1.16 1.16"/>
            `
            : ``;

    // `size` is baked into `width`/`height` here, so the renderer's template cache
    // holds one entry per (icon, color, stroke, size, badge) combination.
    // Long-term goal (Strategy F follow-up, see PERFORMANCE.md §10 Q5) is to render
    // at a fixed natural size and apply `size` as a per-marker attribute/transform update,
    // collapsing the cache to ~1 entry per collection. That change requires F's
    // stable-DOM-node infrastructure to preserve the anchor-offset invariant.
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 57 56">
                <path fill="var(--pin-fill, #FFFFFF)" stroke="var(--pin-stroke, #000000)" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" paint-order="stroke fill" d="M18 15a16 16 0 0 1 3.91 31.52.3.3 0 0 0-.16.13l-3.53 7.21a.25.25 0 0 1-.44 0l-3.53-7.21a.3.3 0 0 0-.17-.13A16 16 0 0 1 18 15"/>
                ${iconSvgString}
                <!--Markup badge-->
                ${markupBadgeSvgString}
            </svg>`;
}
